import { cloneValue, createNodeId } from './common';
import {
    normalizeExecutionOutputs,
    resolveExecutionSelectedOutputId,
    markExecutionPrimaryOutput,
} from './execution';

export const createDuplicatedNodeId = (node) => {
    if (node?.type === 'functionNode') return createNodeId('func');
    if (node?.type === 'groupNode') return createNodeId('group-node');
    return createNodeId('image-node');
};

export const normalizeDuplicatedLatestExecution = (latestExecution, selectedOutputId) => {
    if (!latestExecution || typeof latestExecution !== 'object') return null;

    const nextExecution = cloneValue(latestExecution);
    const outputs = normalizeExecutionOutputs(nextExecution.outputs);
    if (!outputs.length) return null;
    const nextSelectedOutputId = resolveExecutionSelectedOutputId(outputs, selectedOutputId);

    return {
        ...nextExecution,
        id: '',
        executionId: '',
        nodeExecutionId: '',
        execSerial: undefined,
        taskSerial: undefined,
        progress: 100,
        status: nextExecution.status === 'processing' ? 'succeeded' : (nextExecution.status || 'succeeded'),
        errCode: '',
        errMsg: '',
        outputs: markExecutionPrimaryOutput(outputs, nextSelectedOutputId),
        selectedOutputId: nextSelectedOutputId,
    };
};

export const appendDuplicateSuffix = (value, fallbackValue = '节点') => {
    const normalizedValue = String(value || '').trim() || fallbackValue;
    return `${normalizedValue}副本`;
};

export const remapDuplicatedPanelState = (panelState = {}, nodeIdMap = new Map(), edgeIdMap = new Map()) => {
    if (!panelState || typeof panelState !== 'object') return panelState;

    const nextPanelState = cloneValue(panelState);

    if (Array.isArray(nextPanelState.linkedUploads)) {
        nextPanelState.linkedUploads = nextPanelState.linkedUploads
            .map((item) => {
                const nextEdgeId = edgeIdMap.get(item?.edgeId);
                if (!nextEdgeId) return null;

                return {
                    ...item,
                    edgeId: nextEdgeId,
                    sourceNodeId: nodeIdMap.get(item?.sourceNodeId) || item?.sourceNodeId,
                };
            })
            .filter(Boolean);
    }

    if (
        nextPanelState.linkedSlotSources
        && typeof nextPanelState.linkedSlotSources === 'object'
        && !Array.isArray(nextPanelState.linkedSlotSources)
    ) {
        nextPanelState.linkedSlotSources = Object.fromEntries(
            Object.entries(nextPanelState.linkedSlotSources)
                .map(([slotKey, value]) => {
                    if (!value || typeof value !== 'object') {
                        return [slotKey, value];
                    }

                    const nextEdgeId = edgeIdMap.get(value?.edgeId);
                    if (!nextEdgeId) {
                        return null;
                    }

                    return [
                        slotKey,
                        {
                            ...value,
                            edgeId: nextEdgeId,
                            sourceNodeId: nodeIdMap.get(value?.sourceNodeId) || value?.sourceNodeId,
                        },
                    ];
                })
                .filter(Boolean),
        );
    }

    return nextPanelState;
};

export const normalizeDuplicatedNodeData = (data, options = {}) => {
    const {
        nodeIdMap = new Map(),
        edgeIdMap = new Map(),
    } = options;
    const nextData = cloneValue(data || {});

    if (!nextData || typeof nextData !== 'object') return {};

    const sourceExecutionStatus = String(
        nextData.latestExecution?.status
        || nextData.executionStatus
        || '',
    ).toLowerCase();
    const isSourceProcessing = sourceExecutionStatus === 'processing';
    const duplicatedLatestExecution = isSourceProcessing
        ? null
        : normalizeDuplicatedLatestExecution(
            nextData.latestExecution,
            nextData.selectedOutputId,
        );
    const nextSelectedOutputId = isSourceProcessing
        ? ''
        : (duplicatedLatestExecution?.selectedOutputId || nextData.selectedOutputId || '');
    const nextExecutionStatus = isSourceProcessing
        ? 'idle'
        : (duplicatedLatestExecution?.status || nextData.executionStatus || 'idle');
    const nextDisplayMode = isSourceProcessing
        ? 'empty'
        : (
            normalizeExecutionOutputs(duplicatedLatestExecution?.outputs || []).length > 0
                ? 'stacked'
                : (nextData.displayMode || 'empty')
        );

    return {
        ...nextData,
        panelState: remapDuplicatedPanelState(nextData.panelState || {}, nodeIdMap, edgeIdMap),
        activeExecutionId: '',
        groupExecutionLocked: false,
        latestExecution: duplicatedLatestExecution,
        executionStatus: nextExecutionStatus,
        selectedOutputId: nextSelectedOutputId,
        displayMode: nextDisplayMode,
    };
};

export const normalizeDuplicatedProjectNode = (node) => {
    const nextNode = cloneValue(node || {});
    const renderData = nextNode?.renderData && typeof nextNode.renderData === 'object'
        ? nextNode.renderData
        : {};
    const duplicatedRenderNodeData = {
        ...normalizeDuplicatedNodeData(renderData?.data),
        activeExecutionId: '',
        groupExecutionLocked: false,
    };

    return {
        ...nextNode,
        latestExecution: normalizeDuplicatedLatestExecution(
            nextNode?.latestExecution,
            duplicatedRenderNodeData.selectedOutputId,
        ),
        renderData: {
            ...renderData,
            data: duplicatedRenderNodeData,
        },
    };
};